import { useEffect, useRef, useState } from "react";
import {
  DeliveryTypeDisplayName,
  OrderTypesEnum,
} from "../../../../utils/enums";
import { cn } from "../../../../utils/cn";
import { getFranchiseDisplayName } from "../../../../utils/help/localization/localization";
import { useAppSelector } from "../../../../services/hooks/useReduxHooks";
import { useScrollLock } from "../../../../services/hooks/useScrollLock";
import { AddressPlus, Franchise } from "../../../../utils/types";
import { LocationIconFilled } from "../../../screens/S-Pay/PayScreen.icons";
import { SearchInput } from "../../../shared/SearchInput/SearchInput";
import { SavedLocationCard } from "../../../shared/SavedLocationCard/SavedLocationCard";
import {
  CarPickupIcon,
  DineInIcon,
  PickupIcon,
} from "../../../shared/SelectOrderInfo/SelectOrderInfoV3Icons";
import {
  UseDeliverySelectionDrawerProps,
  useDeliverySelectionDrawer,
} from "../useDeliverySelectionDrawer";
import styles from "./DeliverySelectionDrawerV2.module.scss";

type OrderTypeTab = OrderTypesEnum | "car_pickup" | `delivery_type_${number}`;

function getFranchiseOrderTypeIcons(franchise: Franchise) {
  const config = franchise.franchiseConfig;
  const icons: { key: string; Icon: () => JSX.Element }[] = [];

  const deliveryTypeDisplayNames = new Set(
    (config?.deliveryV2 ?? []).map((c) => c.deliveryType.displayName)
  );

  if (deliveryTypeDisplayNames.has(DeliveryTypeDisplayName.CAR_PICKUP)) {
    icons.push({ key: "car_pickup", Icon: CarPickupIcon });
  }
  if (config?.takeOut) {
    icons.push({ key: "takeout", Icon: PickupIcon });
  }
  if (config?.dineIn) {
    icons.push({ key: "dine_in", Icon: DineInIcon });
  }

  return icons;
}

export const DeliverySelectionDrawerV2 = (
  props: UseDeliverySelectionDrawerProps
) => {
  const {
    drawerState,
    delivery,
    isDeliveryDrawerInverted,
    addresses,
    handleApplyDelivery,
    handleSelectAddress,
    handleDeleteAddress,
    handleCloseDrawer,
    handleAddNewLocation,
    selectAddressTranslation,
    outsideDeliveryTranslation,
    uniqueDeliveryTypes,
    hasTakeOut,
    hasDineIn,
    selectedTab,
    setSelectedTab,
    searchQuery,
    handleBranchSearchChange,
    selectedFranchiseId,
    selectedFranchiseForTab,
    setSelectedFranchiseId,
    filteredFranchises,
    isDeliveryTab,
    handleApplyFranchise,
    t,
  } = useDeliverySelectionDrawer(props);

  const hasWelcomeDrawerV3 = useAppSelector(
    (state) => state.config.restaurantConfig?.hasWelcomeDrawerV3
  );
  const isV3 = !!hasWelcomeDrawerV3;

  // Same reason as the shared BottomSheet: this drawer and its backdrop are
  // `position: fixed`, so letting the document scroll underneath them slides
  // them out of step with the visible viewport on mobile browsers.
  useScrollLock(drawerState);

  // undefined = user hasn't interacted (falls back to delivery.selectedAddress)
  // null      = user explicitly deselected
  // AddressPlus = user selected an address
  const [pendingAddress, setPendingAddress] = useState<
    AddressPlus | null | undefined
  >(undefined);

  // Reset selection whenever the drawer opens
  useEffect(() => {
    if (drawerState) {
      setPendingAddress(undefined);
    }
  }, [drawerState]);

  const tabButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Scroll the selected tab into view whenever selection changes — same
  // pattern as SelectOrderInfoV3 / MapScreenV2.
  useEffect(() => {
    if (selectedTab) {
      tabButtonRefs.current[selectedTab]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [selectedTab]);

  // The footer only casts its shadow while something is actually scrolling
  // underneath it. Which element scrolls depends on the tab (.franchise_list on
  // a branch tab, the address list on delivery), so measure whichever
  // descendant scrollers exist rather than naming one.
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [footerScrolled, setFooterScrolled] = useState(false);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }

    // Resolve which descendants actually scroll ONCE per tab/open — the
    // getComputedStyle-over-every-descendant walk is too heavy to run on every
    // scroll event (review note). Scroll then only re-reads the cached
    // elements' sizes; resize (infrequent) re-resolves in case the DOM changed.
    let scrollers: HTMLElement[] = [];
    const resolveScrollers = () => {
      scrollers = Array.from(content.querySelectorAll<HTMLElement>("*")).filter(
        (el) => {
          const overflowY = getComputedStyle(el).overflowY;

          return overflowY === "auto" || overflowY === "scroll";
        }
      );
    };

    const measure = () => {
      setFooterScrolled(
        scrollers.some((el) => el.scrollHeight > el.clientHeight + 1)
      );
    };

    const onResize = () => {
      resolveScrollers();
      measure();
    };

    resolveScrollers();
    measure();
    // capture:true — scroll events don't bubble, so this catches them from
    // whichever descendant actually scrolls.
    content.addEventListener("scroll", measure, {
      passive: true,
      capture: true,
    });
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(content);

    return () => {
      content.removeEventListener("scroll", measure, { capture: true });
      resizeObserver.disconnect();
    };
  }, [selectedTab, drawerState]);

  // Build tab buttons — same pattern as SelectOrderInfoV3
  type TabButton = {
    type: OrderTypeTab;
    label: string;
    // displayName-based for delivery types (stable across environments), the
    // order-type string for takeout/dine-in.
    testId: string;
  };

  const TAB_BUTTONS: TabButton[] = [
    ...uniqueDeliveryTypes.map(({ id, displayName }) => ({
      type: (displayName === DeliveryTypeDisplayName.CAR_PICKUP
        ? "car_pickup"
        : `delivery_type_${id}`) as OrderTypeTab,
      label: t(`table-page:${displayName.toLowerCase()}`),
      testId: `delivery-drawer-tab-${displayName}`,
    })),
    ...(hasTakeOut
      ? [
          {
            type: OrderTypesEnum.TAKE_OUT as OrderTypeTab,
            label: t("table-page:table-take-out"),
            testId: `delivery-drawer-tab-${OrderTypesEnum.TAKE_OUT}`,
          },
        ]
      : []),
    ...(hasDineIn
      ? [
          {
            type: OrderTypesEnum.DINE_IN as OrderTypeTab,
            label: t("table-page:table-dine-in"),
            testId: `delivery-drawer-tab-${OrderTypesEnum.DINE_IN}`,
          },
        ]
      : []),
  ];

  // When untouched (undefined), fall back to the currently applied address if
  // it's visible in this tab's list — so switching delivery types with the same
  // address doesn't leave Apply dimmed.
  const effectiveAddress: AddressPlus | null =
    pendingAddress !== undefined
      ? pendingAddress
      : addresses.find((a) => a.id === delivery.selectedAddress?.id) ?? null;

  // Derive the active tab's delivery type ID for address filtering
  const tabDeliveryTypeId = (() => {
    if (selectedTab === "car_pickup") {
      return uniqueDeliveryTypes.find(
        (dt) => dt.displayName === DeliveryTypeDisplayName.CAR_PICKUP
      )?.id;
    }
    if (
      typeof selectedTab === "string" &&
      selectedTab.startsWith("delivery_type_")
    ) {
      return Number(selectedTab.replace("delivery_type_", ""));
    }

    return undefined;
  })();

  // Selecting a branch highlights only the icon for the order type currently
  // being booked — the card's other order-type icons stay grey, so the card
  // doesn't read as "all of these are selected". Keys match
  // getFranchiseOrderTypeIcons above; mirrors MapScreenV2's activeOrderIconKey
  // (prepits/project#3913 item 9, which was only applied to the location screen).
  const activeOrderIconKey: string | undefined =
    selectedTab === "car_pickup"
      ? "car_pickup"
      : selectedTab === OrderTypesEnum.TAKE_OUT
      ? "takeout"
      : selectedTab === OrderTypesEnum.DINE_IN
      ? "dine_in"
      : undefined;

  // selectedFranchiseForTab, not selectedFranchiseId: the id survives a tab
  // switch even when that branch doesn't offer the newly selected order type,
  // which left Apply enabled with nothing visibly selected.
  const isApplyDisabled =
    isV3 && !isDeliveryTab ? !selectedFranchiseForTab : !effectiveAddress;

  if (!drawerState) {
    return null;
  }

  return (
    <>
      <div className={styles.backdrop} onClick={handleCloseDrawer} />
      <div
        data-testid="delivery-drawer-wrapper"
        className={cn(
          styles.drawer_wrapper,
          isDeliveryDrawerInverted && "invert"
        )}
        style={{
          animationName: drawerState ? styles.open_drawer : styles.close_drawer,
        }}
      >
        {/* Drag handle */}
        <div className={styles.drag_handle} />

        {/* Tab bar — V3 only */}
        {isV3 && TAB_BUTTONS.length > 1 && (
          <div className={styles.tab_bar_wrapper}>
            <div className={styles.tab_bar}>
              <div className={styles.tab_bar_scroll}>
                {TAB_BUTTONS.map(({ type, label, testId }) => (
                  <button
                    key={type}
                    data-testid={testId}
                    ref={(el) => {
                      tabButtonRefs.current[type] = el;
                    }}
                    type="button"
                    className={cn(
                      styles.tab,
                      selectedTab === type && styles.tab_active
                    )}
                    onClick={() => {
                      setSelectedTab(type);
                      setPendingAddress(undefined);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className={styles.drawer_wrapper__content} ref={contentRef}>
          {isV3 && !isDeliveryTab ? (
            // ── V3 non-delivery tab: search + franchise list ───────────────
            <>
              <div className={styles.search_wrapper}>
                <SearchInput
                  value={searchQuery}
                  onChange={(e) => handleBranchSearchChange(e.target.value)}
                  placeholder={t("common:search")}
                  testId="delivery-drawer-search"
                />
              </div>
              {searchQuery.trim() && filteredFranchises.length === 0 && (
                <p
                  className={styles.no_branches}
                  data-testid="delivery-drawer-no-branches"
                >
                  {t("common:no-branches-by-this-name")}
                </p>
              )}
              <ul className={styles.franchise_list}>
                {filteredFranchises.map((franchise) => (
                  <li
                    key={franchise.id}
                    data-testid={`delivery-drawer-franchise-${franchise.slug}`}
                    className={cn(
                      styles.franchise_item,
                      selectedFranchiseId === franchise.id &&
                        styles.franchise_item_selected
                    )}
                    onClick={() => setSelectedFranchiseId(franchise.id)}
                  >
                    <span className={styles.franchise_name}>
                      {getFranchiseDisplayName(franchise)}
                    </span>
                    <div className={styles.franchise_icons}>
                      {getFranchiseOrderTypeIcons(franchise).map(
                        ({ key, Icon }) => (
                          <span
                            key={key}
                            className={cn(
                              styles.franchise_icon,
                              key === activeOrderIconKey &&
                                styles.franchise_icon_active
                            )}
                          >
                            <Icon />
                          </span>
                        )
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            // ── Delivery tab: saved addresses UI ──────────────────────────
            <>
              <h2 className={cn(styles.title, "main_title")}>
                {selectAddressTranslation}
              </h2>
              <div className={styles.addresses}>
                {addresses
                  .filter(
                    (address: AddressPlus) =>
                      !isV3 ||
                      address.deliveryTypeId === tabDeliveryTypeId ||
                      !tabDeliveryTypeId
                  )
                  .map((address: AddressPlus, index: number) => (
                    <SavedLocationCard
                      key={address.addressTitle + index}
                      title={address.addressTitle}
                      details={
                        address.streetName
                          ? `${address.streetName}, ${address.area}`
                          : `B-${address.buildingNumber}, F-${address.floorNumber}`
                      }
                      isSelected={effectiveAddress?.id === address.id}
                      isAvailable={
                        address.isInCurrentFranchise ||
                        (address.availableDeliveryFranchises !== undefined &&
                          address.availableDeliveryFranchises.length > 0)
                      }
                      unavailableText={
                        !address.isInCurrentFranchise &&
                        address.availableDeliveryFranchises?.length === 0
                          ? outsideDeliveryTranslation
                          : undefined
                      }
                      onSelect={() =>
                        setPendingAddress(
                          address.id !== effectiveAddress?.id ? address : null
                        )
                      }
                      onDelete={() => handleDeleteAddress(address)}
                      testId="address-details"
                      titleTestId={`address-title-${index}`}
                    />
                  ))}
                <button
                  type="button"
                  className={styles.add_new_location}
                  onClick={handleAddNewLocation}
                  data-testid="add-new-address"
                >
                  <span className={styles.add_new_location__icon}>
                    <LocationIconFilled color="var(--primary-color)" />
                  </span>
                  <span className={styles.add_new_location__text}>
                    {t("delivery:add-new-address")}
                  </span>
                </button>
              </div>
            </>
          )}

          {/* Footer */}
          <div
            className={cn(
              styles.action_buttons,
              footerScrolled && styles.action_buttons__scrolled
            )}
          >
            <button
              type="button"
              data-testid="delivery-drawer-cancel"
              className={cn(styles.button, styles.button__cancel)}
              onClick={() => {
                setPendingAddress(undefined);
                handleCloseDrawer();
              }}
            >
              {t("common:cancel")}
            </button>
            <button
              type="button"
              data-testid="delivery-drawer-apply"
              className={cn(
                styles.button,
                styles.button__apply,
                isApplyDisabled && styles.button__disabled
              )}
              disabled={isApplyDisabled}
              onClick={() => {
                if (isV3 && !isDeliveryTab) {
                  handleApplyFranchise();
                } else {
                  if (!effectiveAddress) {
                    return;
                  }
                  // V3 commits the address and navigates to home; legacy just
                  // closes the drawer and keeps the user on the current page
                  // (e.g. cart), matching the pre-V3 behavior.
                  if (isV3) {
                    handleApplyDelivery(effectiveAddress);
                  } else {
                    handleSelectAddress(effectiveAddress);
                  }
                  setPendingAddress(undefined);
                }
              }}
            >
              {t("common:apply")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
